"use client";

import * as React from "react";
import { Trash2, AlertCircle, RefreshCw, Calendar, Clock, MapPin, Monitor } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import moment from "moment-timezone";
import { resolveDisplayTimezone } from "../../lib/checkout/booking-window";
import type { BasketUnavailability } from "@obby/lib";
import { BasketItemStatus } from "./basket-item-status";

type BasketItemCardProps = {
  id: string;
  type: "booking" | "product" | "giftcard" | "bundle";
  title: string;
  subtitle?: string;
  imageUrl?: string;
  price: number;
  quantity: number;
  currency?: string;
  sessionDate?: string;
  /** Pre-formatted window, e.g. "09:30–12:30 (1h 30m)". See formatBookingWindow. */
  sessionTime?: string;
  /** The row's ALREADY-RESOLVED display zone — see `resolveRowTimezone`, which
   *  the drawer calls once so this date and the `sessionTime` beside it cannot
   *  land on different clocks. This component must not re-decide it: it does
   *  not receive the fields that decision reads.
   *  Without it the date is read off the BROWSER's
   *  clock, which names the wrong day for a customer far enough east or west --
   *  and would then disagree with `sessionTime`, which is always the listing's. */
  timezone?: string;
  isOnline?: boolean;
  location?: string;
  tickets?: Array<{
    name: string;
    quantity: number;
    price: number;
    /**
     * Replaces the default "{quantity} x {name}" line. Set by the host for
     * items whose quantity is not self-describing next to the ticket name —
     * a rental ticket named after its rate ("1 hour") reads as a duration
     * when rendered "2 x 1 hour", so the host spells out units instead.
     */
    label?: string;
    /** Full line amount in smallest currency unit (e.g. pence). From v2 / cart pricing; overrides price × quantity when set. */
    lineTotal?: number;
  }>;
  addOns?: Array<{ name: string; quantity: number; price: number; groupName?: string | null }>;
  /** Line-level charges that sit OUTSIDE `price` (postage, gift wrap). Already
   *  scaled for the line — rendered as-is, never multiplied by quantity. */
  surcharges?: Array<{ name: string; amount: number }>;
  isAvailable?: boolean;
  availabilityMessage?: string;
  /** Structured unavailability code; when present takes priority over the legacy availabilityMessage rendering. */
  unavailability?: BasketUnavailability;
  priceChanged?: boolean;
  currentPrice?: number;
  /** When set, the item is a £0 bundle redemption — renders a "Covered by …" badge. */
  redeemedFromBundleName?: string;
  onRemove?: (id: string) => void;
  onUpdatePrice?: (id: string, newPrice: number) => void;
  /** Called with the new quantity when the user accepts a "Reduce to N" auto-fix. */
  onReduceQuantity?: (id: string, newQuantity: number) => void;
  /** Called when the user clicks Retry on a transient validation error. */
  onRetryValidation?: (id: string) => void;
  /** Called when the user clicks Join membership on a members-only ticket. */
  onJoinMembership?: (id: string) => void;
  /** Called when the user clicks "See other sessions" on a cancelled item. */
  onSeeAlternatives?: (id: string) => void;
  className?: string;
};

function formatPrice(price: number, currency: string = "GBP"): string {
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(price / 100);
  } catch {
    return `${currency} ${(price / 100).toFixed(2)}`;
  }
}

function formatDate(dateString: string, timezone?: string): string {
  // ⚠ Resolved, not taken on trust. `school.timezone` is a free-form string
  // with no enum and `moment.tz` does not throw on a name it has no data for —
  // it logs and formats in UTC. `resolveRowTimezone` puts an ONLINE row on
  // the viewer's clock and otherwise falls back to the
  // browser's zone, which is the same clock the window beside this is rendered
  // on, so the two halves of a row cannot name different days.
  const zone = resolveDisplayTimezone(timezone);
  const m = moment.tz(dateString, zone);
  // "Thu 18 Jun" — the shape `toLocaleDateString("en-GB", {weekday, day,
  // month})` produced before this, verified rather than assumed.
  if (m.isValid()) return m.format("ddd D MMM");
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/**
 * The status panel's primary button ("Reduce to N", "Retry", "Join
 * membership") renders only when this returns a handler — `BasketItemStatus`
 * guards on `onPrimaryAction`, so `undefined` is how a button that could not
 * do anything is kept off the screen.
 *
 * It used to be an unconditional inline arrow whose body optional-chained the
 * callbacks. The button therefore ALWAYS rendered, and clicking it silently
 * did nothing wherever the host app had not wired that particular callback —
 * on the very verdicts whose entire UX is that one action. Both apps shipped a
 * dead button that way: the webcomponents embed wires none of the three, and
 * the widget wires no `onJoinMembership`.
 *
 * Exported for tests only.
 */
export function resolvePrimaryAction(
  u: BasketUnavailability,
  id: string,
  handlers: Pick<
    BasketItemCardProps,
    "onReduceQuantity" | "onRetryValidation" | "onJoinMembership"
  >,
  hasTickets: boolean,
): (() => void) | undefined {
  switch (u.code) {
    case "SESSION_PARTIAL_CAPACITY":
    case "PRODUCT_PARTIAL_STOCK": {
      // Withheld from a ticketed line, where it cannot work. "Reduce to N"
      // writes the item's `quantity`, but the server derives the quantity it
      // measures against capacity from `ticketSelections` whenever the item
      // carries them (ValidateBasketItems, `requestedQuantity`). So the
      // reduction never reaches the check it claims to satisfy: the next
      // validate returns the identical verdict, while this card's header total
      // switches to `price × N` beside ticket rows that still read the old
      // quantity. Removing the item is the only honest recovery, and the
      // panel's Remove stays visible for `partial`.
      if (hasTickets) return undefined;
      const { onReduceQuantity } = handlers;
      if (!onReduceQuantity) return undefined;
      return () => onReduceQuantity(id, u.availableQuantity);
    }
    case "VALIDATION_TRANSIENT": {
      const { onRetryValidation } = handlers;
      return onRetryValidation ? () => onRetryValidation(id) : undefined;
    }
    case "TICKET_MEMBERS_ONLY": {
      const { onJoinMembership } = handlers;
      return onJoinMembership ? () => onJoinMembership(id) : undefined;
    }
    default:
      return undefined;
  }
}

function BasketItemCard({
  id,
  type,
  title,
  subtitle,
  imageUrl,
  price,
  quantity,
  currency = "GBP",
  sessionDate,
  sessionTime,
  timezone,
  isOnline,
  location,
  tickets,
  addOns,
  surcharges,
  isAvailable = true,
  availabilityMessage,
  unavailability,
  priceChanged = false,
  currentPrice,
  redeemedFromBundleName,
  onRemove,
  onUpdatePrice,
  onReduceQuantity,
  onRetryValidation,
  onJoinMembership,
  onSeeAlternatives,
  className,
}: BasketItemCardProps) {
  const isUnavailable = isAvailable === false;

  return (
    <div
      className={cn(
        "relative rounded-lg border bg-card overflow-hidden",
        // Structured unavailability owns its own tone via BasketItemStatus.
        // Only paint the card border red on the legacy unstructured path.
        unavailability && "opacity-90",
        isUnavailable && !unavailability && "opacity-75 border-destructive",
        priceChanged && "border-warning",
        className
      )}
    >
      <div className="flex gap-3 p-3">
        {/* Thumbnail */}
        {imageUrl && (
          <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md">
            <img
              src={imageUrl}
              alt={title}
              className="h-full w-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Type badge */}
          <span
            className={cn(
              "inline-block text-xs font-medium uppercase px-2 py-0.5 rounded mb-1",
              type === "booking" && "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
              type === "product" && "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
              type === "giftcard" && "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
            )}
          >
            {type === "giftcard" ? "Gift Card" : type}
          </span>

          {/* Title */}
          <h4 className="font-semibold text-foreground truncate">{title}</h4>

          {/* Subtitle / Course name */}
          {subtitle && (
            <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
          )}

          {/* Bundle redemption: "Covered by …" badge. */}
          {redeemedFromBundleName !== undefined && (
            <span className="mt-1 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
              Covered by {redeemedFromBundleName || "your bundle"}
            </span>
          )}

          {/* Session date/time for bookings */}
          {type === "booking" && sessionDate && (
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {formatDate(sessionDate, timezone)}
              </span>
              {sessionTime && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {sessionTime}
                </span>
              )}
            </div>
          )}

          {/* Location */}
          {type === "booking" && (
            <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
              {isOnline ? (
                <>
                  <Monitor className="h-3.5 w-3.5" />
                  <span>Online</span>
                </>
              ) : location ? (
                <>
                  <MapPin className="h-3.5 w-3.5" />
                  <span className="truncate">{location}</span>
                </>
              ) : null}
            </div>
          )}

          {/* Tickets breakdown */}
          {tickets && tickets.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {tickets.map((ticket, idx) => (
                <div
                  key={idx}
                  className="flex justify-between gap-2 text-sm text-muted-foreground"
                >
                  <span>{ticketLineLabel(ticket)}</span>
                  <span>
                    {formatPrice(
                      ticket.lineTotal !== undefined
                        ? ticket.lineTotal
                        : ticket.price * ticket.quantity,
                      currency,
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Add-ons breakdown */}
          {addOns && addOns.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {addOns.map((addOn, idx) => (
                <div
                  key={idx}
                  className="flex justify-between gap-2 text-sm text-muted-foreground"
                >
                  <span>
                    {addOn.quantity} x {addOn.groupName ? `${addOn.groupName}: ${addOn.name}` : addOn.name}
                  </span>
                  <span>{formatPrice(addOn.price * addOn.quantity, currency)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Line surcharges (postage / gift wrap) — charged ON TOP of the
              item price, which is why the drawer total adds them. */}
          {surcharges && surcharges.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {surcharges.map((surcharge, idx) => (
                <div
                  key={idx}
                  className="flex justify-between gap-2 text-sm text-muted-foreground"
                >
                  <span>{surcharge.name}</span>
                  <span>{formatPrice(surcharge.amount, currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Price and remove button */}
        <div className="flex flex-col items-end justify-between">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => onRemove?.(id)}
            aria-label="Remove item"
          >
            <Trash2 className="h-4 w-4" />
          </Button>

          <div className="text-right">
            {priceChanged && currentPrice !== undefined ? (
              <div className="space-y-0.5">
                <p className="text-sm text-muted-foreground line-through">
                  {formatPrice(price * quantity, currency)}
                </p>
                <p className="font-semibold text-foreground">
                  {formatPrice(currentPrice * quantity, currency)}
                </p>
              </div>
            ) : (
              <p className="font-semibold text-foreground">
                {formatPrice(price * quantity, currency)}
              </p>
            )}
            {quantity > 1 && !tickets?.length && (
              <p className="text-xs text-muted-foreground">
                {formatPrice(price, currency)} each
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Unavailable warning */}
      {unavailability ? (
        <BasketItemStatus
          unavailability={unavailability}
          onPrimaryAction={resolvePrimaryAction(
            unavailability,
            id,
            { onReduceQuantity, onRetryValidation, onJoinMembership },
            !!tickets?.length,
          )}
          onSecondaryAction={
            onSeeAlternatives ? () => onSeeAlternatives(id) : undefined
          }
          onRemove={onRemove ? () => onRemove(id) : undefined}
        />
      ) : isUnavailable ? (
        // Legacy fallback: server hasn't been upgraded to emit `unavailability`
        // yet. Remove this branch once the API change is shipped everywhere.
        <div className="bg-destructive/10 border-t border-destructive/20 px-3 py-2">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">
                {availabilityMessage || "This item is no longer available"}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 h-7 text-xs"
                onClick={() => onRemove?.(id)}
              >
                Remove from basket
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Price changed warning */}
      {priceChanged && currentPrice !== undefined && !isUnavailable && !unavailability && (
        <div className="bg-warning/10 border-t border-warning/20 px-3 py-2">
          <div className="flex items-start gap-2">
            <RefreshCw className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-warning">
                Price changed from {formatPrice(price, currency)} to{" "}
                {formatPrice(currentPrice, currency)}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 h-7 text-xs"
                onClick={() => onUpdatePrice?.(id, currentPrice)}
              >
                Update price
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { BasketItemCard };
/** The ticket line's text: a host-supplied label, else "{quantity} x {name}". */
export function ticketLineLabel(ticket: {
  name: string;
  quantity: number;
  label?: string;
}): string {
  return ticket.label ?? `${ticket.quantity} x ${ticket.name}`;
}

/**
 * Rental hires: quantity is UNITS of kit, spelled out so it cannot read as
 * hours, and the rate-named ticket carries its fraction multiplier so the
 * line explains the price beside it — "2 units · 2 × 1 hour". A single
 * fraction drops the multiplier ("2 units · 1 hour").
 */
export function rentalTicketLineLabel(
  quantity: number,
  ticketName: string,
  fractions?: number,
): string {
  const units = `${quantity} ${quantity === 1 ? "unit" : "units"}`;
  const rate = fractions && fractions > 1 ? `${fractions} × ${ticketName}` : ticketName;
  return `${units} · ${rate}`;
}

export type { BasketItemCardProps };
